import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ChevronDown, 
  ArrowRight, 
  TrendingUp, 
  LayoutGrid, 
  RefreshCcw, 
  Layers, 
  LucideIcon,
  Target,
  Zap,
  Building2,
  Rocket,
  Megaphone,
  Share2,
  Search,
  Mail,
  Users,
  BarChart3,
  Database,
  Brain,
  Cloud,
  BookOpen,
  FileText,
  HelpCircle,
  FolderOpen
} from "lucide-react";
import logo from "@/assets/xperfomax-logo.png";
import MobileServicesNav from "./MobileServicesNav";
import TabletServicesNav from "./TabletServicesNav";
import MobileBottomCTA from "./MobileBottomCTA";

interface ServiceGroup {
  label: string;
  description: string;
  icon: LucideIcon;
  items: { label: string; href: string }[];
}

interface DropdownConfig {
  type: 'mega' | 'simple';
  groups?: ServiceGroup[];
  items?: { label: string; href: string; icon?: LucideIcon; description?: string }[];
}

const serviceGroups: ServiceGroup[] = [
  {
    label: "Acquire",
    description: "Reach the right people",
    icon: TrendingUp,
    items: [
      { label: "Paid Search", href: "/paid-search" },
      { label: "Paid Social", href: "/paid-social" },
      { label: "Programmatic", href: "/programmatic" },
      { label: "SEO", href: "/seo" },
    ],
  },
  {
    label: "Convert",
    description: "Turn attention into action",
    icon: LayoutGrid,
    items: [
      { label: "CRO", href: "/cro" },
      { label: "Landing Pages", href: "/landing-pages" },
      { label: "Web Development", href: "/web-development" },
      { label: "UX Strategy", href: "/ux-strategy" },
      { label: "Creative", href: "/creative" },
    ],
  },
  {
    label: "Retain",
    description: "Revenue beyond first sale",
    icon: RefreshCcw,
    items: [
      { label: "Email Marketing", href: "/email-marketing" },
      { label: "Lifecycle", href: "/lifecycle-marketing" },
      { label: "Loyalty", href: "/loyalty-programs" },
      { label: "CRM", href: "/crm-strategy" },
    ],
  },
  {
    label: "Scale",
    description: "Momentum that compounds",
    icon: Layers,
    items: [
      { label: "Analytics", href: "/analytics" },
      { label: "Attribution", href: "/services" },
      { label: "Data Infrastructure", href: "/data-management" },
      { label: "Strategy", href: "/services" },
    ],
  },
];

const solutionsDropdown: DropdownConfig = {
  type: 'simple',
  items: [
    { label: "E-commerce Growth", href: "/solutions/ecommerce-growth", icon: Target, description: "Scale online retail" },
    { label: "SaaS Acceleration", href: "/solutions/saas-acceleration", icon: Zap, description: "Pipeline & retention" },
    { label: "D2C Scale", href: "/solutions/d2c-scale", icon: Rocket, description: "Startup to scale-up" },
    { label: "Enterprise Performance", href: "/solutions/enterprise-performance", icon: Building2, description: "Agility at scale" },
  ],
};

const mediaDropdown: DropdownConfig = {
  type: 'simple',
  items: [
    { label: "Paid Marketing", href: "/paid-marketing", icon: Megaphone, description: "Paid search & social" },
    { label: "Social Media", href: "/social-media", icon: Share2, description: "Organic growth" },
    { label: "SEO", href: "/seo", icon: Search, description: "Search visibility" },
    { label: "Email Marketing", href: "/email-marketing", icon: Mail, description: "Retention & lifecycle" },
    { label: "Affiliate Marketing", href: "/affiliate-marketing", icon: Users, description: "Partner networks" },
  ],
};

const analyticsDropdown: DropdownConfig = {
  type: 'simple',
  items: [
    { label: "Analytics", href: "/analytics", icon: BarChart3, description: "Data & insights" },
    { label: "Data Management", href: "/data-management", icon: Database, description: "Infrastructure" },
    { label: "AI Automation", href: "/ai-automation", icon: Brain, description: "Intelligent systems" },
    { label: "Google Cloud Platform", href: "/google-cloud-platform", icon: Cloud, description: "Cloud solutions" },
  ],
};

const resourcesDropdown: DropdownConfig = {
  type: 'simple',
  items: [
    { label: "Tools", href: "/tools", icon: Target, description: "Free marketing calculators" },
    { label: "Blog", href: "/blog", icon: BookOpen, description: "Insights & updates" },
    { label: "Guides", href: "/guides", icon: FileText, description: "In-depth resources" },
    { label: "Case Studies", href: "/case-studies", icon: FolderOpen, description: "Client success stories" },
    { label: "FAQs", href: "/faqs", icon: HelpCircle, description: "Common questions" },
  ],
};

type NavItemKey = 'services' | 'solutions' | 'media' | 'analytics' | 'resources';

const navItems: { label: string; href: string; dropdownKey?: NavItemKey }[] = [
  { label: "Services", href: "/services", dropdownKey: 'services' },
  { label: "Solutions", href: "/solutions", dropdownKey: 'solutions' },
  { label: "Media Activations", href: "/media-activations", dropdownKey: 'media' },
  { label: "Analytics", href: "/analytics", dropdownKey: 'analytics' },
  { label: "Resources", href: "/resources", dropdownKey: 'resources' },
  { label: "About", href: "/about" },
];

const dropdowns: Record<NavItemKey, DropdownConfig> = {
  services: { type: 'mega', groups: serviceGroups },
  solutions: solutionsDropdown,
  media: mediaDropdown,
  analytics: analyticsDropdown,
  resources: resourcesDropdown,
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<NavItemKey | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedItems, setMobileExpandedItems] = useState<NavItemKey[]>([]);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpandedItems([]);
  }, [location]);

  const toggleMobileItem = (key: NavItemKey) => {
    setMobileExpandedItems(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const renderSimpleDropdown = (config: DropdownConfig, parentHref: string) => {
    if (config.type !== 'simple' || !config.items) return null;
    
    return (
      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
        <div className="bg-background border border-border shadow-xl min-w-[280px]">
          <div className="p-2">
            {config.items.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className="flex items-start gap-3 p-3 hover:bg-muted/50 transition-colors group"
                >
                  {IconComponent && (
                    <IconComponent className="h-4 w-4 mt-0.5 text-muted-foreground group-hover:text-accent transition-colors" strokeWidth={1.5} />
                  )}
                  <div>
                    <span className="text-sm font-medium text-foreground block">
                      {item.label}
                    </span>
                    {item.description && (
                      <span className="text-xs text-muted-foreground">
                        {item.description}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="border-t border-border p-2">
            <Link 
              to={parentHref}
              className="flex items-center justify-center gap-1 p-2 text-xs font-medium text-primary hover:text-primary/80 group"
            >
              View all
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  const renderMegaDropdown = (config: DropdownConfig) => {
    if (config.type !== 'mega' || !config.groups) return null;
    
    return (
      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
        <div className="bg-background border border-border shadow-xl min-w-[700px]">
          {/* Header */}
          <div className="px-8 py-5 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  Growth Systems
                </span>
                <p className="text-sm text-foreground mt-1">
                  Capabilities organized by growth objective
                </p>
              </div>
              <Link 
                to="/services" 
                className="text-xs font-medium text-primary hover:text-primary/80 flex items-center gap-1 group"
              >
                View all
                <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
          
          {/* Service Groups */}
          <div className="grid grid-cols-4 gap-0">
            {config.groups.map((group, index) => {
              const IconComponent = group.icon;
              return (
                <div 
                  key={group.label} 
                  className={`p-6 group/card ${index < config.groups!.length - 1 ? 'border-r border-border' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <IconComponent 
                      className="h-4 w-4 text-muted-foreground group-hover/card:text-accent transition-colors" 
                      strokeWidth={1.5}
                    />
                    <span className="text-sm font-medium text-foreground">
                      {group.label}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground block mb-4 pl-6">
                    {group.description}
                  </span>
                  <ul className="space-y-2 pl-6">
                    {group.items.map((subItem) => (
                      <li key={subItem.label}>
                        <Link
                          to={subItem.href}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors block py-0.5"
                        >
                          {subItem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || mobileMenuOpen
          ? "bg-background border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="container-wide">
        <nav className={`flex items-center justify-between transition-all duration-300 ${
          isScrolled ? 'h-14 md:h-14' : 'h-16 md:h-20'
        }`}>
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center mr-6 hover:opacity-90 transition-opacity"
          >
            <img 
              src={logo} 
              alt="XperfoMax" 
              width={120}
              height={64}
              className={`w-auto transition-all duration-300 ${
                isScrolled ? 'h-9 md:h-14' : 'h-10 md:h-16'
              }`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const hasDropdown = !!item.dropdownKey;
              const isDropdownOpen = activeDropdown === item.dropdownKey;
              const dropdownConfig = item.dropdownKey ? dropdowns[item.dropdownKey] : null;
              
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.dropdownKey && setActiveDropdown(item.dropdownKey)}
                  onMouseLeave={() => item.dropdownKey && setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`relative text-sm px-3 py-2 transition-colors flex items-center gap-1 group ${
                      location.pathname === item.href
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {item.label}
                    {hasDropdown && (
                      <ChevronDown className={`h-3 w-3 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                    )}
                    {/* Hover underline - accent color only */}
                    <span className={`absolute bottom-0 left-3 right-3 h-px transition-transform origin-left ${
                      location.pathname === item.href 
                        ? "bg-primary scale-x-100" 
                        : "bg-accent scale-x-0 group-hover:scale-x-100"
                    }`} />
                  </Link>

                  {/* Dropdown */}
                  {hasDropdown && isDropdownOpen && dropdownConfig && (
                    dropdownConfig.type === 'mega' 
                      ? renderMegaDropdown(dropdownConfig)
                      : renderSimpleDropdown(dropdownConfig, item.href)
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Button size="sm" asChild>
              <Link to="/contact">Contact</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2 -mr-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className={`w-5 h-0.5 bg-foreground transition-all duration-200 ${mobileMenuOpen ? "rotate-45 translate-y-1" : ""}`} />
            <div className={`w-5 h-0.5 bg-foreground mt-1.5 transition-all duration-200 ${mobileMenuOpen ? "-rotate-45 -translate-y-0.5" : ""}`} />
          </button>
        </nav>

        {/* Mobile Menu (< 768px) */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-border max-h-[calc(100vh-4rem-80px)] overflow-y-auto pb-24">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const hasDropdown = !!item.dropdownKey;
                const isExpanded = item.dropdownKey ? mobileExpandedItems.includes(item.dropdownKey) : false;
                const dropdownConfig = item.dropdownKey ? dropdowns[item.dropdownKey] : null;
                
                // Special handling for Services - use new component
                if (item.dropdownKey === 'services') {
                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => toggleMobileItem('services')}
                        className={`w-full flex items-center justify-between py-3 text-left ${
                          location.pathname === item.href
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        <span className="text-lg">{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                      {isExpanded && (
                        <div className="pl-2 pb-4">
                          <MobileServicesNav onNavigate={() => setMobileMenuOpen(false)} />
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div key={item.label}>
                    {hasDropdown && dropdownConfig ? (
                      <>
                        <button
                          onClick={() => item.dropdownKey && toggleMobileItem(item.dropdownKey)}
                          className={`w-full flex items-center justify-between py-3 text-left ${
                            location.pathname === item.href
                              ? "text-primary"
                              : "text-foreground"
                          }`}
                        >
                          <span className="text-lg">{item.label}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        {isExpanded && (
                          <div className="pl-4 pb-4 space-y-2">
                            {dropdownConfig.items?.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className="block min-h-[48px] py-3 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-3 text-lg ${
                          location.pathname === item.href
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        )}

        {/* Tablet Menu (768px - 1023px) */}
        {mobileMenuOpen && (
          <div className="hidden md:block lg:hidden py-6 border-t border-border max-h-[calc(100vh-4rem-80px)] overflow-y-auto pb-24">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const hasDropdown = !!item.dropdownKey;
                const isExpanded = item.dropdownKey ? mobileExpandedItems.includes(item.dropdownKey) : false;
                const dropdownConfig = item.dropdownKey ? dropdowns[item.dropdownKey] : null;
                
                // Special handling for Services - use tablet component
                if (item.dropdownKey === 'services') {
                  return (
                    <div key={item.label}>
                      <button
                        onClick={() => toggleMobileItem('services')}
                        className={`w-full flex items-center justify-between py-3 text-left ${
                          location.pathname === item.href
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        <span className="text-lg">{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                      {isExpanded && (
                        <div className="py-4">
                          <TabletServicesNav onNavigate={() => setMobileMenuOpen(false)} />
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <div key={item.label}>
                    {hasDropdown && dropdownConfig ? (
                      <>
                        <button
                          onClick={() => item.dropdownKey && toggleMobileItem(item.dropdownKey)}
                          className={`w-full flex items-center justify-between py-3 text-left ${
                            location.pathname === item.href
                              ? "text-primary"
                              : "text-foreground"
                          }`}
                        >
                          <span className="text-lg">{item.label}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        {isExpanded && (
                          <div className="pl-4 pb-4 space-y-2">
                            {dropdownConfig.items?.map((subItem) => (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                className="block min-h-[48px] py-3 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-3 text-lg ${
                          location.pathname === item.href
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        )}
      </div>

      {/* Sticky Mobile CTA */}
      <MobileBottomCTA visible={mobileMenuOpen} />
    </header>
  );
};

export default Header;
